import React, { createContext, useContext, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useAppState } from "../../../context/StateContext";
import { PurchaseOrder } from "../../../models/purchase.order";
import { TolerancePercentage } from "../../../models/tolerancePercentage";
import PurchaseOrderService from "../../../services/purchase.order.service";
import { PurchaseOrderKeys } from "../../../util/queries";
import { useShipmentContext } from "../hooks/useShipmentContext";

interface ShipmentOrdersRecourseContextProps {
  orderList: PurchaseOrder[];
  refetchOrderList: () => void;
  tolerance?: TolerancePercentage | undefined;
  isLoading: boolean;
}

const ShipmentOrdersRecourseContext = createContext<ShipmentOrdersRecourseContextProps>(
  {} as ShipmentOrdersRecourseContextProps
);

interface ShipmentOrdersRecourseProviderProps {
  children: React.ReactChild;
}

const ShipmentOrdersRecourseProvider = ({ children }: ShipmentOrdersRecourseProviderProps) => {
  const { currentUser } = useAppState();
  const { shipment } = useShipmentContext();

  const filters = useMemo(() => {
    return {
      ...(shipment?.shipmentType ? { mode: [shipment?.shipmentType?.toUpperCase()] } : {}),
      ...(shipment?.origin || shipment?.portOfLoading
        ? { origin: [shipment?.portOfLoading?.siteCode || shipment?.origin?.toString()] }
        : {}),
      ...(shipment?.destination || shipment?.portOfDischarge
        ? {
            destination: [shipment?.portOfDischarge?.siteCode || shipment?.destination?.toString()],
          }
        : {}),
      ...(shipment?.incoterm ? { incoterm: [shipment?.incoterm?.toString()] } : {}),
      ...(shipment?.purchaseOrders && shipment?.purchaseOrders?.length > 0
        ? { orders: [...shipment.purchaseOrders.map((order) => String(order.id))] }
        : {}),
    };
  }, [
    shipment?.shipmentType,
    shipment?.origin,
    shipment?.destination,
    shipment?.portOfLoading,
    shipment?.portOfDischarge,
    shipment?.purchaseOrders,
    shipment?.incoterm,
  ]);

  const includeAvailablePurchaseOrders = JSON.stringify([
    { association: "supplier", attributes: ["name"], include: [{ association: "location" }] },
    { association: "consignee", attributes: ["name"] },
    { association: "purchaseOrderProducts", include: [{ association: "product" }] },
  ]);

  const {
    data: orderList,
    isInitialLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: PurchaseOrderKeys.getAvailableShipments([
      filters?.mode,
      filters?.origin,
      filters?.destination,
      currentUser?.companyId,
      filters?.orders,
    ]),
    queryFn: async () => {
      const response = await PurchaseOrderService.getAvailableShipmentList({
        companyId: currentUser?.companyId,
        include: includeAvailablePurchaseOrders,
        ...filters,
      });

      if (response && response.data) return response.data.rows;
    },
    enabled:
      !!currentUser?.companyId && !!filters?.mode && !!filters?.origin && !!filters?.destination,
  });

  return (
    <ShipmentOrdersRecourseContext.Provider
      value={{
        orderList: orderList || [],
        isLoading: isInitialLoading || isRefetching,
        refetchOrderList: refetch,
      }}
    >
      {children}
    </ShipmentOrdersRecourseContext.Provider>
  );
};

export { ShipmentOrdersRecourseContext, ShipmentOrdersRecourseProvider };

export const useShipmentOrdersRecourse = () => {
  const context = useContext(ShipmentOrdersRecourseContext);

  return context;
};
